import { supabase } from '../lib/supabase';

export interface Payment {
  id: string;
  user_id: string;
  diagnostic_id: string | null;
  stripe_payment_intent_id: string | null;
  stripe_session_id: string | null;
  amount: number;
  currency: string;
  status: 'pending' | 'succeeded' | 'failed';
  created_at: string;
  updated_at: string;
}

export async function createPaymentRecord(
  userId: string,
  stripePaymentIntentId: string,
  amount: number,
  diagnosticId?: string
): Promise<Payment> {
  const { data, error } = await supabase
    .from('payments')
    .insert({
      user_id: userId,
      diagnostic_id: diagnosticId || null,
      stripe_payment_intent_id: stripePaymentIntentId,
      amount,
      currency: 'eur',
      status: 'pending'
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating payment record:', error);
    throw new Error('Failed to create payment record');
  }

  return data;
}

export async function updatePaymentStatus(
  stripePaymentIntentId: string,
  status: 'succeeded' | 'failed'
): Promise<void> {
  const { error } = await supabase
    .from('payments')
    .update({ status })
    .eq('stripe_payment_intent_id', stripePaymentIntentId);

  if (error) {
    console.error('Error updating payment status:', error);
    throw new Error('Failed to update payment status');
  }
}

export async function getUserPayments(userId: string): Promise<Payment[]> {
  const { data, error } = await supabase
    .from('payments')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching user payments:', error);
    throw new Error('Failed to fetch payments');
  }

  return data || [];
}

export async function getPaymentByIntent(
  stripePaymentIntentId: string
): Promise<Payment | null> {
  const { data, error } = await supabase
    .from('payments')
    .select('*')
    .eq('stripe_payment_intent_id', stripePaymentIntentId)
    .maybeSingle();

  if (error) {
    console.error('Error fetching payment:', error);
    return null;
  }

  return data;
}

export interface VerifyPaymentResult {
  success: boolean;
  error?: string;
  diagnosticId?: string;
  alreadyUnlocked?: boolean;
}

export async function verifyAndUnlockPayment(sessionId: string): Promise<VerifyPaymentResult> {
  try {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

    const apiUrl = `${supabaseUrl}/functions/v1/verify-and-unlock-payment`;

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${supabaseAnonKey}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ sessionId }),
    });

    const result = await response.json();

    if (!response.ok) {
      console.error('Verification failed:', result);
      return {
        success: false,
        error: result.error || 'Failed to verify payment'
      };
    }

    return result;
  } catch (error) {
    console.error('Error calling verify-and-unlock-payment:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Network error'
    };
  }
}

export async function manualUnlockReport(diagnosticId: string): Promise<VerifyPaymentResult> {
  try {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

    const apiUrl = `${supabaseUrl}/functions/v1/manual-unlock`;

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${supabaseAnonKey}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ diagnosticId }),
    });

    const result = await response.json();

    if (!response.ok) {
      console.error('Manual unlock failed:', result);
      return {
        success: false,
        error: result.error || 'Failed to unlock report'
      };
    }

    return result;
  } catch (error) {
    console.error('Error calling manual-unlock:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Network error'
    };
  }
}
